import axios from 'axios'
import posthog from 'posthog-js'

import { UseQueryOptions, useQuery } from '@tanstack/react-query'

import { ChainName, getChainType } from '@src/configs/chainInfo'
import { V2_API_URL } from '@src/configs/env'
import { getChecksumAddress } from '@src/utils/address'
import { isFeatureEnabled } from '@src/utils/environment'
import { createContextLogger } from '@src/utils/logger'
import { mapTokenAddress } from '@src/utils/tokenAddressMapping'

const logger = createContextLogger('useLiquidityCheck')

/**
 * Response from the liquidity check endpoint
 */
interface LiquidityResponse {
    supported: boolean
    sources: string[] // Can include 'marketmaker', 'cctp', etc.
    message: string
}

/**
 * Hook to fetch a quote based on provided parameters.
 *
 * This hook uses TanStack Query to perform a network request to fetch a quote.
 * It automatically handles errors, cancellation, and provides helper methods for
 * invalidating and refetching the query or forcefully canceling an ongoing request.
 *
 * @param {IQuoteParams | undefined} params - The parameters required to fetch a quote.
 *   Must be defined and have a non-zero `srcAmount`.
 * @param {Omit<UseQueryOptions<IQuoteResponse>, 'queryKey' | 'queryFn'>} [options] -
 *   Additional options for configuring the query.
 *
 * @returns {UseQueryResult<IQuoteResponse> & {
 *   data: IQuoteResponse,
 *   invalidateQuoteAndRefetch: (newParams: IQuoteParams) => Promise<void>,
 *   forceCancel: () => Promise<void>
 * }} An object containing the query result and the following helper functions:
 *
 * - **invalidateQuoteAndRefetch:** Invalidates the current quote query and prefetches a new one with updated parameters.
 * - **forceCancel:** Forcefully cancels an ongoing quote query.
 */
/**
 * Hook to check if liquidity is available for a specific token pair
 * Only performs actual checks when V2_MIGRATION is enabled
 *
 * @param srcChain Source chain ID
 * @param srcAssetAddress Source token address
 * @param dstChain Destination chain ID
 * @param dstAssetAddress Destination token address
 * @param options Additional query options
 * @returns Query result with liquidity availability information
 */
export const useLiquidityCheck = (
    srcChain?: string | number,
    srcAssetAddress?: string,
    dstChain?: string | number,
    dstAssetAddress?: string,
    options?: Omit<
        UseQueryOptions<{
            supported: boolean
            sources: string[]
            message: string
            error?: string
        }>,
        'queryKey' | 'queryFn'
    >
) => {
    // Only enable the query if we have all required parameters AND V2_MIGRATION is enabled
    const hasRequiredParams = !!srcChain && !!srcAssetAddress && !!dstChain && !!dstAssetAddress
    const isV2Enabled = isFeatureEnabled('V2_MIGRATION') && !!V2_API_URL
    const isEnabled = hasRequiredParams && isV2Enabled

    // Map token addresses for consistency with API calls
    // This ensures the query key matches what's actually sent to the API
    const mappedSrcAssetAddress = hasRequiredParams
        ? mapTokenAddress(srcAssetAddress!, true, srcChain as ChainName | number)
        : undefined
    const mappedDstAssetAddress = hasRequiredParams
        ? mapTokenAddress(dstAssetAddress!, true, dstChain as ChainName | number)
        : undefined

    return useQuery<{
        supported: boolean
        sources: string[]
        message: string
        error?: string
    }>({
        // Use mapped addresses in the query key to match what's sent to the API
        queryKey: ['liquidityCheck', srcChain, mappedSrcAssetAddress, dstChain, mappedDstAssetAddress],
        queryFn: async ({ signal }) => {
            if (!isEnabled) {
                throw new Error('Missing required parameters for liquidity check')
            }

            return checkLiquidityAvailability(srcChain!, srcAssetAddress!, dstChain!, dstAssetAddress!, signal)
        },
        enabled: isEnabled,
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 60 * 1000, // 1 minute
        refetchOnWindowFocus: false,
        ...options,
    })
}

/**
 * Check if liquidity is available for a given source/destination pair
 * Only available when V2_MIGRATION is enabled
 *
 * @param srcChain Source chain ID
 * @param srcAssetAddress Source token address
 * @param dstChain Destination chain ID
 * @param dstAssetAddress Destination token address
 * @param signal Optional AbortSignal for cancellation
 * @returns Object with supported flag, available sources, and message
 */
export const checkLiquidityAvailability = async (
    srcChain: string | number,
    srcAssetAddress: string,
    dstChain: string | number,
    dstAssetAddress: string,
    signal?: AbortSignal
): Promise<{
    supported: boolean
    sources: string[]
    message: string
    error?: string
}> => {
    const quoteStartTime = performance.now()

    // If V2_MIGRATION is not enabled, always return supported=true
    // since the legacy API doesn't have a liquidity check endpoint
    if (!isFeatureEnabled('V2_MIGRATION') || !V2_API_URL) {
        logger.info('[checkLiquidityAvailability] V2_MIGRATION not enabled, skipping liquidity check')
        return {
            supported: true, // Always assume liquidity is available in V1
            sources: ['legacy'],
            message: 'Using legacy API, liquidity check skipped',
        }
    }

    try {
        // Use the V2 API URL for liquidity checks
        const apiUrl = V2_API_URL

        // Map token addresses using mapTokenAddress function
        // The isQuote=true parameter ensures proper handling of native tokens
        let mappedSrcAssetAddress = mapTokenAddress(srcAssetAddress, true, srcChain as ChainName | number)
        let mappedDstAssetAddress = mapTokenAddress(dstAssetAddress, true, dstChain as ChainName | number)

        // Ensure addresses are checksummed for EVM chains
        if (getChainType(srcChain as number) === 'EVM' && mappedSrcAssetAddress !== 'native') {
            mappedSrcAssetAddress = getChecksumAddress(mappedSrcAssetAddress)
        }

        if (getChainType(dstChain as number) === 'EVM' && mappedDstAssetAddress !== 'native') {
            mappedDstAssetAddress = getChecksumAddress(mappedDstAssetAddress)
        }

        logger.info(
            `[checkLiquidityAvailability] Checking liquidity for ${srcAssetAddress} (mapped: ${mappedSrcAssetAddress}) from ${srcChain} to ${dstChain}`,
            {
                srcChain,
                dstChain,
                srcAssetAddress,
                mappedSrcAssetAddress,
                dstAssetAddress,
                mappedDstAssetAddress,
                usingV2Api: isFeatureEnabled('V2_MIGRATION') && !!V2_API_URL,
            }
        )

        // Create API instance
        const api = axios.create({
            baseURL: apiUrl,
        })

        // Build the URL with query parameters
        const url = `/quotes/liquidity?src_chain_id=${srcChain}&src_token_address=${mappedSrcAssetAddress}&dst_chain_id=${dstChain}&dst_token_address=${mappedDstAssetAddress}`

        // Make the API call
        const response = await api.get<LiquidityResponse>(url, { signal })

        if (response.status === 200) {
            const data = response.data

            logger.info('[checkLiquidityAvailability] Liquidity check response received', {
                supported: data.supported,
                sources: data.sources,
                message: data.message,
                responseTime: `${performance.now() - quoteStartTime}ms`,
            })

            // Track the liquidity check in analytics
            posthog.capture('liquidity_check', {
                src_chain: srcChain,
                dst_chain: dstChain,
                src_asset: srcAssetAddress,
                src_asset_mapped: mappedSrcAssetAddress,
                dst_asset: dstAssetAddress,
                dst_asset_mapped: mappedDstAssetAddress,
                supported: data.supported,
                sources: data.sources,
                check_time_ms: performance.now() - quoteStartTime,
            })

            return {
                supported: data.supported,
                sources: data.sources || [],
                message: data.message || (data.supported ? 'Liquidity available' : 'Liquidity not available'),
            }
        } else {
            logger.error(`[checkLiquidityAvailability] API returned non-200 status: ${response.status}`, {
                status: response.status,
                statusText: response.statusText,
            })

            return {
                supported: false,
                sources: [],
                message: 'Error checking liquidity',
                error: `API error: ${response.statusText}`,
            }
        }
    } catch (error) {
        // Handle errors
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'

        logger.error('[checkLiquidityAvailability] Error checking liquidity', {
            error: errorMessage,
            srcChain,
            dstChain,
        })

        return {
            supported: false,
            sources: [],
            message: 'Error checking liquidity',
            error: errorMessage,
        }
    }
}
